import { daysBetween } from "./calendar.js";

/** Shared display formatting. Kept out of shared/types.js: the server has no
 *  opinion about how many decimal places a person wants to read. */

/**
 * Hours, at the precision a reader actually wants.
 *
 * Minutes below the hour, because a timer stopped after ten minutes reading
 * "0.2h" looks like nothing happened; one decimal up to ten hours; whole hours
 * beyond that, where a tenth is noise.
 */
export function formatHours(h: number): string {
  if (!Number.isFinite(h) || h <= 0) return "0m";
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(h < 10 ? 1 : 0)}h`;
}

/** A token count as Claude Code's own status line writes it: 812, 13.8k, 1.2M. */
export function formatTokens(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return String(Math.round(n));
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** A stretch of work time: 45s, 3m 12s, 1h 05m. Seconds only while they matter. */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor(s / 60) % 60).padStart(2, "0")}m`;
}

/**
 * When a shopping item was last bought, in days as a person counts them:
 * "today", "yesterday", "8 days ago". Calendar days, not 24-hour periods —
 * something bought last night at ten was bought yesterday, even at nine.
 */
export function lastBought(ms: number | null, now: number): string {
  if (ms === null) return "never";
  const days = daysBetween(ms, now);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}
